import React, { useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Radio, RadioGroup, Select, Table, Textarea, TextInput, Toggle } from "@opendate-app/setlist";
import SettingsPageWrapper from "../../../shared/SettingsPageWrapper";
import client from "../../../shared/api/client";
import { toastError, toastSuccess } from "../../../shared/toastHelper";
import "../styles/settingsGiftCards.css";

const STANDARD_FEES = "STANDARD_FEES";
const ABSORB_FEES = "ABSORB_FEES";
const TERMS_MAX_LENGTH = 5000;

const GENERAL_LEDGER_ACCOUNTS = [
  {
    slot: "clearing",
    label: "Gift card clearing",
    hint: "Money Opendate is holding for you from card sales, until it is paid out.",
  },
  { slot: "liability", label: "Gift card liability", hint: "What you still owe fans on cards they haven't spent." },
  {
    slot: "fee",
    label: "Gift card fees",
    hint: "Opendate's fees on gift cards that come out of your balance: fees you absorb on sales, the processing fee a refund leaves behind, and chargeback fees. Any fee you charge buyers goes here too.",
  },
  {
    slot: "promotion",
    label: "Promotional expense",
    hint: "Complimentary cards, and value staff add to or take off a card. When a card expires, its unspent free value is credited back to this account.",
  },
  {
    slot: "breakage",
    label: "Breakage income",
    hint: "Value a fan forfeits when staff void a paid card, and paid value left on a card when it expires, once you confirm an unclaimed property rule that lets you keep it.",
  },
  {
    slot: "unclaimed_property",
    label: "Unclaimed property payable",
    hint: "Paid value your confirmed unclaimed property rule says you report to the state, moved here once a card has gone unused for the rule's dormancy period. The card keeps its balance, and the value moves back if the card is used again.",
  },
];

const EXEMPT = "exempt";
const REPORTABLE = "reportable";
const PARTIAL = "partial";

const TREATMENT_LABELS = {
  [EXEMPT]: "Exempt: you keep unused value",
  [REPORTABLE]: "Reportable: unused value goes to the state",
  [PARTIAL]: "Partial: part of unused value goes to the state",
};

const treatmentOptions = [
  { value: "", label: "Use Opendate's suggestion" },
  ...[EXEMPT, REPORTABLE, PARTIAL].map((value) => ({ value, label: TREATMENT_LABELS[value] })),
];

const UNCLAIMED_PROPERTY_FIELDS = ["holderState", "treatment", "dormancyYears", "reportablePercent"];

const digitsOnly = (value) => String(value || "").replace(/\D/g, "");

const describeRule = (rule) => {
  const parts = [TREATMENT_LABELS[rule.treatment]];
  if (rule.dormancy_years) {
    const start = rule.dormancy_from_year_end ? " (the clock starts no earlier than December 31 of the year sold)" : "";
    parts.push(`after ${rule.dormancy_years} years without use${start}`);
  }
  if (rule.treatment === PARTIAL) parts.push(`${rule.reportable_percent}% reportable`);
  if (rule.reportable_percent_when_expires !== null && rule.reportable_percent_when_expires !== undefined) {
    parts.push(`${rule.reportable_percent_when_expires}% reportable if the card expires`);
  }
  return parts.join(", ");
};

const formatMoney = (amount, currency) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD" }).format(amount);

const feeColumns = (currency) => [
  { key: "amount", header: "Card", render: (row) => formatMoney(row.amount, currency) },
  { key: "fees", header: "Fees", align: "right", render: (row) => formatMoney(row.fees, currency) },
  { key: "buyer_total", header: "Buyer pays", align: "right", render: (row) => formatMoney(row.buyer_total, currency) },
  { key: "team_payout", header: "You receive", align: "right", render: (row) => formatMoney(row.team_payout, currency) },
];

const Section = ({ title, description, children }) => (
  <div className="gift-card-settings-section">
    <div>
      <h3 className="gift-card-settings-section-title">{title}</h3>
      <p className="gift-card-settings-section-description">{description}</p>
    </div>
    <div className="gift-card-settings-section-body">{children}</div>
  </div>
);

Section.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const OptionLabel = ({ title, description }) => (
  <span>
    <span className="gift-card-settings-option-title">{title}</span>
    <span className="gift-card-settings-option-description">{description}</span>
  </span>
);

OptionLabel.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
};

const checkedFrom = (value) => (value && value.target ? value.target.checked : Boolean(value));

const accountsFrom = (generalLedgerAccounts = {}) =>
  GENERAL_LEDGER_ACCOUNTS.reduce(
    (accounts, { slot }) => ({
      ...accounts,
      [slot]: (generalLedgerAccounts || {})[slot] ? String(generalLedgerAccounts[slot]) : "",
    }),
    {}
  );

const unclaimedFrom = (unclaimedProperty = {}) => ({
  holderState: unclaimedProperty.holderState || unclaimedProperty.defaultHolderState || "",
  treatment: unclaimedProperty.treatment || "",
  dormancyYears: unclaimedProperty.dormancyYears ? String(unclaimedProperty.dormancyYears) : "",
  reportablePercent: unclaimedProperty.reportablePercent ? String(unclaimedProperty.reportablePercent) : "",
});

const settingsFrom = ({ giftCardsEnabled, defaultTerms, feeSetting, generalLedgerAccounts, unclaimedProperty }) => ({
  enabled: Boolean(giftCardsEnabled),
  terms: defaultTerms || "",
  feeSetting: feeSetting || STANDARD_FEES,
  accounts: accountsFrom(generalLedgerAccounts),
  unclaimed: unclaimedFrom(unclaimedProperty),
});

const SettingsGiftCards = ({
  giftCardsEnabled,
  defaultTerms,
  feeSetting,
  currency,
  feeExamples = [],
  canAccessGeneralLedgerCodes = false,
  generalLedgerCodes = [],
  generalLedgerAccounts = {},
  unclaimedProperty = {},
  updatePath,
  settingsPath,
  lastEditedAt,
  changelogVersions,
}) => {
  const [saved, setSaved] = useState(() =>
    settingsFrom({ giftCardsEnabled, defaultTerms, feeSetting, generalLedgerAccounts, unclaimedProperty })
  );
  const [unclaimedStatus, setUnclaimedStatus] = useState(unclaimedProperty);
  const [draft, setDraft] = useState(saved);
  const [examples, setExamples] = useState(feeExamples);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const dirty =
    draft.enabled !== saved.enabled ||
    draft.terms !== saved.terms ||
    draft.feeSetting !== saved.feeSetting ||
    GENERAL_LEDGER_ACCOUNTS.some(({ slot }) => draft.accounts[slot] !== saved.accounts[slot]) ||
    UNCLAIMED_PROPERTY_FIELDS.some((field) => draft.unclaimed[field] !== saved.unclaimed[field]);

  const suggestions = unclaimedStatus.suggestions || {};
  const suggestion = suggestions[draft.unclaimed.holderState];
  const stateOptions = [{ value: "", label: "Choose a state" }, ...(unclaimedStatus.states || [])];
  const needsDormancy = [REPORTABLE, PARTIAL].includes(draft.unclaimed.treatment);

  const update = (changes) => setDraft((current) => ({ ...current, ...changes }));

  const updateUnclaimed = (changes) =>
    setDraft((current) => ({ ...current, unclaimed: { ...current.unclaimed, ...changes } }));

  const updateAccount = (slot, value) =>
    setDraft((current) => ({ ...current, accounts: { ...current.accounts, [slot]: value || "" } }));

  const accountParams = () =>
    GENERAL_LEDGER_ACCOUNTS.reduce(
      (params, { slot }) => ({ ...params, [`gift_card_${slot}_general_ledger_code_id`]: draft.accounts[slot] }),
      {}
    );

  const codeOptions = [
    { value: "", label: "No account" },
    ...generalLedgerCodes.map((code) => ({ value: String(code.id), label: `${code.code} — ${code.name}` })),
  ];

  const handleSave = async ({ confirm = false } = {}) => {
    setSaving(true);
    setError(null);
    try {
      const { data } = await client.patch(updatePath, {
        team: {
          gift_cards_enabled: draft.enabled,
          gift_card_default_terms: draft.terms,
          gift_card_fee_setting: draft.feeSetting,
          ...(confirm || draft.unclaimed.holderState !== saved.unclaimed.holderState
            ? { gift_card_holder_state: draft.unclaimed.holderState }
            : {}),
          gift_card_unclaimed_property_treatment: draft.unclaimed.treatment,
          gift_card_dormancy_years: draft.unclaimed.dormancyYears,
          gift_card_reportable_percent: draft.unclaimed.reportablePercent,
          ...(confirm ? { confirm_gift_card_unclaimed_property: true } : {}),
          ...(canAccessGeneralLedgerCodes ? accountParams() : {}),
        },
      });
      const next = settingsFrom(data);
      setSaved(next);
      setDraft(next);
      setExamples(data.feeExamples || []);
      setUnclaimedStatus(data.unclaimedProperty || {});
      toastSuccess(confirm ? "Unclaimed property rule confirmed." : "Gift card settings saved.");
    } catch (requestError) {
      const messages = requestError?.response?.data?.errors;
      setError(messages ? messages.join(" ") : null);
      toastError("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft(saved);
    setError(null);
  };

  return (
    <SettingsPageWrapper
      settingsPath={settingsPath}
      title="Gift cards"
      lastEditedAt={lastEditedAt}
      changelogVersions={changelogVersions}
    >
      <section className="gift-card-settings-card">
        <header className="gift-card-settings-header">
          <h2 className="gift-card-settings-title">Gift cards</h2>
          <p className="gift-card-settings-subtitle">Sell gift cards fans can use on tickets and add-ons.</p>
        </header>

        <Section title="Gift cards" description="Turn gift card sales on or off for the whole team.">
          <Toggle
            id="gift-card-settings-enabled"
            label="Sell gift cards"
            checked={draft.enabled}
            disabled={saving}
            onChange={(value) => update({ enabled: checkedFrom(value) })}
          />
          <p className="gift-card-settings-hint">
            Published products are on sale. Cards already sold keep working either way.
          </p>
        </Section>

        <Section title="Default terms" description="Shown on the purchase page, gift email and at checkout.">
          <Textarea
            id="gift-card-settings-terms"
            label="Default terms"
            rows={4}
            maxLength={TERMS_MAX_LENGTH}
            value={draft.terms}
            disabled={saving}
            onChange={(event) => update({ terms: event.target.value })}
            hint="New products start with these terms. Each product can change them."
          />
        </Section>

        <Section title="Fees" description="The gift card fee rule set for your team.">
          {examples.length > 0 ? (
            <div className="gift-card-settings-fee-table">
              <Table columns={feeColumns(currency)} data={examples} getRowKey={(row) => row.amount} />
            </div>
          ) : (
            <p className="gift-card-settings-hint">
              Gift card fees haven&apos;t been set up for your team yet. Contact Opendate support.
            </p>
          )}

          <div className="gift-card-settings-fee-payer">
            <RadioGroup
              name="gift-card-settings-fee-payer"
              value={draft.feeSetting}
              disabled={saving}
              onChange={(value) => update({ feeSetting: value })}
            >
              <Radio
                id="gift-card-settings-buyer-pays"
                value={STANDARD_FEES}
                label={<OptionLabel title="Buyer pays fees" description="Shown as an Opendate fee line before payment." />}
              />
              <Radio
                id="gift-card-settings-team-absorbs"
                value={ABSORB_FEES}
                label={<OptionLabel title="We absorb fees" description="Taken out of the sale before payout. Buyers pay face value." />}
              />
            </RadioGroup>
          </div>
          <p className="gift-card-settings-hint">
            This only applies to gift card sales. Tickets and add-ons keep their own fee setting. No fee is charged
            when a card is used. Tickets bought with a card carry their normal fees.
          </p>
        </Section>

        <Section
          title="Unclaimed property"
          description="How your state treats gift card value nobody uses, for your books."
        >
          <Select
            id="gift-card-settings-holder-state"
            label="State of incorporation or organization"
            options={stateOptions}
            value={draft.unclaimed.holderState}
            disabled={saving}
            onChange={(value) => updateUnclaimed({ holderState: value || "" })}
            hint="The state your business is incorporated or organized in. Opendate starts its suggestion here, but the card owner's last known address can decide which state's law applies, so confirm with your accountant."
          />
          {draft.unclaimed.holderState &&
            (suggestion ? (
              <p className="gift-card-settings-hint" data-testid="gift-card-settings-suggestion">
                Opendate suggests: {describeRule(suggestion)}. Source:{" "}
                <a href={suggestion.source_url} target="_blank" rel="noopener noreferrer">
                  {suggestion.citation}
                </a>
              </p>
            ) : (
              <p className="gift-card-settings-hint" data-testid="gift-card-settings-suggestion">
                Opendate has no suggested rule for this state. Choose a treatment below.
              </p>
            ))}
          <Select
            id="gift-card-settings-treatment"
            label="Treatment"
            options={treatmentOptions}
            value={draft.unclaimed.treatment}
            disabled={saving}
            onChange={(value) => updateUnclaimed({ treatment: value || "" })}
          />
          {needsDormancy && (
            <TextInput
              id="gift-card-settings-dormancy-years"
              label="Years without use before it's reportable"
              inputMode="numeric"
              value={draft.unclaimed.dormancyYears}
              disabled={saving}
              onChange={(event) => updateUnclaimed({ dormancyYears: digitsOnly(event.target.value) })}
            />
          )}
          {draft.unclaimed.treatment === PARTIAL && (
            <TextInput
              id="gift-card-settings-reportable-percent"
              label="Percent of unused value that's reportable"
              inputMode="numeric"
              value={draft.unclaimed.reportablePercent}
              disabled={saving}
              onChange={(event) => updateUnclaimed({ reportablePercent: digitsOnly(event.target.value) })}
            />
          )}
          <p className="gift-card-settings-hint">
            Opendate suggests this from public state summaries. Confirm it with your accountant.
          </p>
          <div className="gift-card-settings-confirm">
            <p className="gift-card-settings-hint" data-testid="gift-card-settings-confirmation">
              {unclaimedStatus.confirmedRule && `Confirmed ${unclaimedStatus.confirmedAt}: ${describeRule(unclaimedStatus.confirmedRule)}.`}
              {unclaimedStatus.needsReconfirming &&
                "Opendate's suggestion for your state has changed since you confirmed it. Review it and confirm again."}
              {!unclaimedStatus.confirmedRule &&
                !unclaimedStatus.needsReconfirming &&
                "Not confirmed. Until you confirm a rule, paid value left on expired or unused cards stays in gift card liability. Confirming posts it to breakage income or unclaimed property payable from then on."}
            </p>
            <Button
              variant="secondary"
              onClick={() => handleSave({ confirm: true })}
              disabled={saving || !draft.unclaimed.holderState}
            >
              Confirm
            </Button>
          </div>
        </Section>

        {canAccessGeneralLedgerCodes && (
          <Section
            title="General ledger accounts"
            description="Where gift card money posts on the General Ledger report."
          >
            {generalLedgerCodes.length === 0 ? (
              <p className="gift-card-settings-hint">
                Your chart of accounts is empty. Add account codes from an event&apos;s ticketing settings, then come
                back and map them here.
              </p>
            ) : (
              <div className="gift-card-settings-accounts">
                {GENERAL_LEDGER_ACCOUNTS.map(({ slot, label, hint }) => (
                  <Select
                    key={slot}
                    id={`gift-card-settings-${slot}-account`}
                    label={label}
                    hint={hint}
                    options={codeOptions}
                    value={draft.accounts[slot]}
                    disabled={saving}
                    onChange={(value) => updateAccount(slot, value)}
                  />
                ))}
              </div>
            )}
            <p className="gift-card-settings-hint">
              A card sale credits the liability and debits the clearing account. Spending a card debits the liability,
              and the revenue comes from the ticket. Anything left unmapped posts under &quot;Unassigned&quot;.
            </p>
          </Section>
        )}

        {error && (
          <div className="gift-card-settings-alert">
            <Alert color="error" title="That didn't save" description={error} />
          </div>
        )}

        <footer className="gift-card-settings-footer">
          <Button variant="secondary" onClick={handleCancel} disabled={!dirty || saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => handleSave()} disabled={!dirty || saving}>
            Save
          </Button>
        </footer>
      </section>
    </SettingsPageWrapper>
  );
};

SettingsGiftCards.propTypes = {
  giftCardsEnabled: PropTypes.bool,
  defaultTerms: PropTypes.string,
  feeSetting: PropTypes.oneOf([STANDARD_FEES, ABSORB_FEES]),
  currency: PropTypes.string,
  feeExamples: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.number.isRequired,
      fees: PropTypes.number.isRequired,
      buyer_total: PropTypes.number.isRequired,
      team_payout: PropTypes.number.isRequired,
    })
  ),
  canAccessGeneralLedgerCodes: PropTypes.bool,
  generalLedgerCodes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      code: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  generalLedgerAccounts: PropTypes.objectOf(PropTypes.number),
  unclaimedProperty: PropTypes.shape({
    holderState: PropTypes.string,
    defaultHolderState: PropTypes.string,
    treatment: PropTypes.string,
    dormancyYears: PropTypes.number,
    reportablePercent: PropTypes.number,
    confirmedAt: PropTypes.string,
    confirmedRule: PropTypes.object,
    needsReconfirming: PropTypes.bool,
    suggestions: PropTypes.objectOf(PropTypes.object),
    states: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })),
  }),
  updatePath: PropTypes.string.isRequired,
  settingsPath: PropTypes.string,
  lastEditedAt: PropTypes.string,
  changelogVersions: PropTypes.array,
};

export default SettingsGiftCards;
